import { mergeAttributes } from '@tiptap/core';
import { Table, TableCell, TableHeader, TableRow, TableView, type TableOptions } from '@tiptap/extension-table';
import type { Node as ProseMirrorNode } from '@tiptap/pm/model';
import type { EditorState } from '@tiptap/pm/state';
import { findTable, rowIsHeader, TableMap } from '@tiptap/pm/tables';

export const TABLE_BORDERLESS_CLASS = 'table-borderless';

declare module '@tiptap/core' {
    interface Commands<ReturnType> {
        templateTable: { toggleTableBorderless: () => ReturnType };
    }
}

export interface TableLayout {
    borderless: boolean;
    headerRow: boolean;
}

export function tableLayoutAt(state: EditorState): TableLayout {
    const table = findTable(state.selection.$from);
    if (!table) return { borderless: false, headerRow: false };
    return {
        borderless: table.node.attrs.borderless === true,
        headerRow: rowIsHeader(TableMap.get(table.node), table.node, 0),
    };
}

class TemplateTableView extends TableView {
    update(node: ProseMirrorNode): boolean {
        if (!super.update(node)) return false;
        this.table.classList.toggle(TABLE_BORDERLESS_CLASS, node.attrs.borderless === true);
        return true;
    }
}

const NEUTRAL_ATTRIBUTE = { default: null, parseHTML: () => null, renderHTML: () => ({}) };

const neutralisedCellAttributes = (parent: Record<string, unknown> | undefined) => ({
    ...parent,
    colwidth: NEUTRAL_ATTRIBUTE,
    align: NEUTRAL_ATTRIBUTE,
});

export const TemplateTable = Table.extend({
    addOptions() {
        return { ...(this.parent?.() as TableOptions), View: TemplateTableView };
    },
    addAttributes() {
        return {
            ...this.parent?.(),
            borderless: {
                default: false,
                parseHTML: (element: HTMLElement) => element.classList.contains(TABLE_BORDERLESS_CLASS),
                renderHTML: (attributes: Record<string, unknown>) => (attributes.borderless ? { class: TABLE_BORDERLESS_CLASS } : {}),
            },
        };
    },
    addCommands() {
        return {
            ...this.parent?.(),
            toggleTableBorderless:
                () =>
                ({ state, tr, dispatch }) => {
                    const table = findTable(state.selection.$from);
                    if (!table) return false;
                    if (dispatch) tr.setNodeMarkup(table.pos, undefined, { ...table.node.attrs, borderless: !table.node.attrs.borderless });
                    return true;
                },
        };
    },
    renderHTML({ HTMLAttributes }) {
        return ['table', mergeAttributes(this.options.HTMLAttributes, HTMLAttributes), ['tbody', 0]];
    },
    addKeyboardShortcuts() {
        return {
            ...this.parent?.(),
            Tab: () => this.editor.commands.goToNextCell(),
            'Shift-Tab': () => this.editor.commands.goToPreviousCell(),
        };
    },
});

export const TemplateTableCell = TableCell.extend({
    addAttributes() {
        return neutralisedCellAttributes(this.parent?.());
    },
});

export const TemplateTableHeader = TableHeader.extend({
    addAttributes() {
        return neutralisedCellAttributes(this.parent?.());
    },
});

export const TEMPLATE_TABLE_EXTENSIONS = [TemplateTable, TableRow, TemplateTableCell, TemplateTableHeader];
